import { useMemo, useState } from "react";
import uniqueId from "lodash/uniqueId";
import { Cell, Label, Pie, PieChart, type PieProps, Sector } from "recharts";
import { type PolarViewBox } from "recharts/types/util/types";

import {
  type ChartConfig,
  ChartContainer,
} from "@vega-core/components/shadcn/chart";
import { getChartColors } from "@vega-core/helpers/getChartColors";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { formatNumber } from "@vega-core/utils/formatNumber";

import { CustomLegend } from "./CustomLegend";
import { EmptyStatePieChart } from "./EmptyStatePieChart";

const renderActiveShape = (props: PieProps) => {
  const {
    cx = 0,
    cy = 0,
    innerRadius = 0,
    outerRadius = 0,
    startAngle = 0,
    endAngle = 0,
    fill = "",
  } = props;

  return (
    <g>
      <Sector
        cx={Number(cx)}
        cy={Number(cy)}
        endAngle={endAngle}
        fill={fill}
        innerRadius={Number(innerRadius)}
        outerRadius={Number(outerRadius) + 10}
        startAngle={startAngle}
      />
    </g>
  );
};

export type CompositionSegment = {
  name?: string;
  value?: number;
  displayValue: string;
  color?: string;
  isOther?: boolean;
};

export function CompositionPieChart({
  composition = [],
  title = "",
  centerLabel,
  variant = "vertical",
  centerVariant = "highest",
  legendVariant = "single",
  emptyState,
  colors,
  noOrdering = false,
  showLegend = true,
}: {
  composition?: CompositionSegment[];
  variant?: "horizontal" | "vertical";
  title?: string;
  centerLabel?: string;
  centerVariant?: "highest" | "total";
  legendVariant?: "double" | "single";
  emptyState?: CompositionSegment[];
  colors?: string[];
  noOrdering?: boolean;
  showLegend?: boolean;
}) {
  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);
  const [hoveredSegment, setHoveredSegment] =
    useState<CompositionSegment | null>(null);

  const defaultValue = useMemo(
    () => ({
      name: m.common_noDataAvailable(),
      value: 0,
      displayValue: "0%",
    }),
    [],
  );

  const sortedComposition = useMemo(() => {
    if (noOrdering) {
      return composition;
    }

    return composition.toSorted(
      (a, b) =>
        Number(Boolean(a.isOther)) - Number(Boolean(b.isOther)) ||
        (b.value ?? 0) - (a.value ?? 0),
    );
  }, [composition, noOrdering]);

  const highestValueItem = useMemo(
    () =>
      composition.length > 0
        ? composition.reduce((prev, current) =>
            (prev.value ?? 0) > (current.value ?? 0) ? prev : current,
          )
        : defaultValue,
    [composition, defaultValue],
  );

  const totalValue = useMemo(
    () => composition.reduce((prev, current) => prev + (current.value ?? 0), 0),
    [composition],
  );

  const getConfig = (composition: CompositionSegment[]): ChartConfig => {
    const config: ChartConfig = {};

    composition.forEach((item, index) => {
      if (item.name && item.value) {
        config[item.name] = {
          label: item.name,
          color: item.color ?? colors?.[index] ?? getChartColors(index),
        };
      }
    });

    return config;
  };

  const isHorizontal = variant === "horizontal";
  const shouldShowEmptyState = composition.length === 0 || totalValue === 0;

  if (shouldShowEmptyState && emptyState) {
    return (
      <EmptyStatePieChart
        centerLabel={centerLabel}
        composition={emptyState}
        legendVariant={legendVariant}
        title={title}
        variant={variant}
      />
    );
  }

  return (
    <div className="w-full flex flex-col gap-4">
      <div
        className={cnTwMerge("p-0 md:px-4 md:pb-4", {
          "flex flex-col md:flex-row gap-4 p-0 items-center": isHorizontal,
          "flex flex-col gap-4 p-0 items-center": !isHorizontal,
        })}
      >
        <div className="flex flex-col items-center gap-2">
          <div className="flex h-12 items-center">
            <p className="body-semi-large-semibold text-center">{title}</p>
          </div>
          <ChartContainer
            className="mx-auto aspect-square h-56"
            config={getConfig(sortedComposition)}
          >
            <PieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
              <Pie
                activeIndex={activeIndex}
                activeShape={(props: unknown) =>
                  renderActiveShape(props as PieProps)
                }
                data={sortedComposition}
                dataKey="value"
                endAngle={-270}
                innerRadius="60%"
                nameKey="name"
                onMouseEnter={(_, index) => {
                  setActiveIndex(index);
                  setHoveredSegment(sortedComposition[index]);
                }}
                onMouseLeave={() => {
                  setActiveIndex(undefined);
                  setHoveredSegment(null);
                }}
                outerRadius="100%"
                startAngle={90}
                strokeWidth={0}
              >
                {sortedComposition.map((item, index) => (
                  <Cell
                    className="bg-opacity-20"
                    fill={
                      item.color ?? colors?.[index] ?? getChartColors(index)
                    }
                    key={uniqueId()}
                  />
                ))}
                <Label
                  content={({ viewBox }) => {
                    const { cx = 0, cy = 0 } = viewBox as PolarViewBox;
                    const defaultItem =
                      centerVariant === "highest"
                        ? highestValueItem
                        : {
                            name: centerLabel ?? "Total",
                            value: totalValue,
                            displayValue: formatNumber(totalValue),
                          };
                    const displayItem = hoveredSegment || defaultItem;
                    const h = 160;
                    const w = 120;

                    const displayValue =
                      centerVariant === "highest"
                        ? displayItem.displayValue
                        : formatNumber(displayItem.value);

                    const displayName =
                      !hoveredSegment && centerLabel
                        ? centerLabel
                        : (displayItem.name ?? "unknown");

                    return (
                      <g>
                        <circle
                          cx={cx}
                          cy={cy}
                          fill="transparent"
                          onMouseEnter={() => {
                            setActiveIndex(undefined);
                            setHoveredSegment(null);
                          }}
                          r={60}
                        />
                        <foreignObject
                          height={h}
                          style={{
                            textAlign: "center",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            pointerEvents: "none",
                          }}
                          width={w}
                          x={cx - w / 2}
                          y={cy - h / 1.85}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              height: "100%",
                              width: "100%",
                            }}
                          >
                            <p
                              className="fill-text/primary heading-4"
                              style={{
                                margin: 0,
                                padding: 0,
                              }}
                            >
                              {displayValue}
                            </p>
                            <p
                              className="body-small-regular text-text/secondary"
                              style={{
                                margin: 0,
                                marginTop: "0.25rem",
                                padding: "0 0.5rem",
                                textAlign: "center",
                                wordBreak: "break-word",
                                lineHeight: "1.25rem",
                                width: "100%",
                              }}
                            >
                              {displayName}
                            </p>
                          </div>
                        </foreignObject>
                      </g>
                    );
                  }}
                />
              </Pie>
            </PieChart>
          </ChartContainer>
        </div>
        {showLegend ? (
          <div
            className={cnTwMerge(
              "grid gap-x-6 gap-y-1 w-full mt-2",
              legendVariant === "double"
                ? "grid-cols-1 sm:grid-cols-2"
                : "grid-cols-1",
            )}
          >
            {sortedComposition.map((item, index) => (
              <CustomLegend
                color={item.color ?? colors?.[index] ?? getChartColors(index)}
                displayValue={item.displayValue}
                key={uniqueId()}
                name={item.name}
                shouldShowEmptyState={shouldShowEmptyState}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
